import React, {
  useCallback, useEffect, useState, Fragment, ReactElement,
} from 'react';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import DealScheduleTasks from '../deal-schedule-tasks/deal-schedule-tasks.component';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import { DEAL_SCHEDULE_DELETE, DEAL_SCHEDULE_EDIT } from '../../../constants/entity/permissions';
import { TAB_INDEX_DEFAULT } from '../../../constants/system/tab-indexes';

const DealSchedules: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isNewDeal,
    isRightDrawerOpen,
    isVisible,
    dealId,
    dealSchedulesData,
    dealScheduleZoom,
    tabIndex,
    tableColumnSettings,
    currentUser,
    onSetTabIndex,
    onChange,
    onZoomChange,
    onTabChange,
    onBeforeTabChange,
    onTableColumnsChange,
    onDeleteClick,
  } = props;

  const permissionList: string[] = useCurrentUserPermissions();

  const formPrefix: string = 'dealData.dealSchedulesData';

  const [currentTab, setCurrentTab]: [string | null, Function] = useState(null);

  const tabs: TabItem[] = dealSchedulesData && !!dealSchedulesData.length
    ? dealSchedulesData.map((dealSchedule: DealSchedule, dealScheduleIndex: number): TabItem => {
      const formKeyDealSchedule: string = FormService.getIndexedFormFieldKey(dealSchedule.id);

      return {
        id: dealSchedule.id,
        name: `deal-schedule-${dealSchedule.id}`,
        title: dealSchedule.name,
        tabComponent: (isEditMode || isNewDeal) && tabIndex === dealScheduleIndex
        && StructureService.checkPermission(DEAL_SCHEDULE_EDIT, permissionList) ? (
          <FormFieldText
            name={`${formPrefix}[${formKeyDealSchedule}].name`}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              event.stopPropagation();
            }}
            onKeyUp={(event: React.KeyboardEvent): void => {
              event.preventDefault();
            }}
          />
        ) : undefined,
        component: (
          <DealScheduleTasks
            dealScheduleIndex={dealScheduleIndex}
            tabIndex={tabIndex}
            dealScheduleData={dealSchedule}
            isEditMode={isEditMode}
            isNewDeal={isNewDeal}
            dealId={dealId}
            onChange={onChange}
            onZoomChange={onZoomChange}
            dealScheduleZoom={dealScheduleZoom}
            currentUser={currentUser}
            tableColumnSettings={tableColumnSettings}
            onTableColumnsChange={onTableColumnsChange}
          />
        ),
      };
    }) : [];

  const deleteClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealScheduleId: number,
  ): void => {
    if (onDeleteClick) {
      onDeleteClick(dealScheduleId);
    }

    event.stopPropagation();
  }, []);

  useEffect((): void => {
    if (tabIndex === null && onSetTabIndex) {
      onSetTabIndex(TAB_INDEX_DEFAULT);
    }

    if (tabs && tabs.length) {
      const currentTabItem: TabItem | undefined = tabs.find((
        tabItem: TabItem, tabIndexInternal: number,
      ): boolean => (
        tabIndexInternal === tabIndex
      ));

      if (currentTabItem) {
        setCurrentTab(currentTabItem.name);
      }
    }
  }, [tabIndex]);

  return (
    <Fragment>
      {
        tabs && !!tabs.length && isVisible && (
          <PageTabs
            isSubTabs
            isEditable={isEditMode || isNewDeal}
            isDeletable={!isEditMode && !isNewDeal}
            isCompressed={isRightDrawerOpen}
            tabs={tabs}
            currentTab={currentTab}
            deletePermission={DEAL_SCHEDULE_DELETE}
            onTabChange={onTabChange}
            onBeforeTabChange={onBeforeTabChange}
            onSetTabIndex={onSetTabIndex}
            onDeleteClick={deleteClick}
          />
        )
      }
      {
        (!tabs || (tabs && !tabs.length)) && (
          <NoContentBlock>
            No schedules data
          </NoContentBlock>
        )
      }
    </Fragment>
  );
});

export default DealSchedules;
